/**
 * @file useScreenPreference.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-28
 * @modified 2026-10-28
 * @description A screen's last choice, remembered for the PERSON across
 *              sessions and devices (Cam, 28 Oct 2026, on the pricing
 *              screens' date range: "only default on first use of the pricing
 *              and costing component, and just remember whatever they've last
 *              chosen regarding date range thereafter").
 *
 *   const [month, setMonth, { loaded, ready }] =
 *     useScreenPreference('repPricing.month', currentMonth);
 *
 *   The stored value comes from GET /api/dashboard/preferences/screens (one
 *   read per page load, shared by every screen); `setValue` changes it at
 *   once on screen and saves it (PUT …/screens/:key) a moment later. Until
 *   `loaded` the value is the default — a screen that fetches by it should
 *   wait for `loaded`, or it fetches twice. Before
 *   migrations/user_screen_preferences.sql nothing is stored (`ready`
 *   false) and every visit opens on the default, as it did before.
 *   The same user_preferences row as useUserPreferences (theme, wallpaper),
 *   its own column, so neither overwrites the other.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../config';

const SAVE_AFTER_MS = 400;

// One read per page load, shared: { promise, prefs, ready }.
let store = null;

function loadAll() {
  if (store) return store.promise;
  store = { prefs: {}, ready: false, done: false, promise: null };
  store.promise = fetch(`${API_URL}/api/dashboard/preferences/screens`, { credentials: 'include' })
    .then(r => (r.ok ? r.json() : { ready: false, prefs: {} }))
    .then(data => {
      store.prefs = (data && data.prefs) || {};
      store.ready = !!(data && data.ready);
      store.done = true;
      return store;
    })
    .catch(() => { store.done = true; return store; });
  return store.promise;
}

/** For tests: forget what was read. */
export function _resetScreenPreferences() {
  store = null;
}

/**
 * @param {string} key       "<screen>.<what>", e.g. "repPricing.month"
 * @param {*} defaultValue   used until something is stored (first use)
 * @param {{ accept?: (v) => boolean }} [o]  a stored value that fails this is ignored
 * @returns {[*, Function, { loaded: boolean, ready: boolean }]}
 */
export default function useScreenPreference(key, defaultValue, { accept = null } = {}) {
  const [value, setValueState] = useState(defaultValue);
  const valueRef = useRef(defaultValue);
  const [loaded, setLoaded] = useState(false);
  const [ready, setReady] = useState(false);
  const timer = useRef(null);
  const touched = useRef(false);
  const acceptRef = useRef(accept);
  acceptRef.current = accept;

  useEffect(() => {
    let live = true;
    loadAll().then(s => {
      if (!live) return;
      const stored = s.prefs ? s.prefs[key] : undefined;
      const ok = stored !== undefined && stored !== null && (!acceptRef.current || acceptRef.current(stored));
      // A choice made before the read came back stands over the stored one.
      if (ok && !touched.current) { valueRef.current = stored; setValueState(stored); }
      setReady(!!s.ready);
      setLoaded(true);
    });
    return () => { live = false; };
  }, [key]);

  // Leaving the screen saves a choice still waiting to go.
  const pending = useRef(null);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
    if (pending.current) pending.current();
  }, []);

  const setValue = useCallback((next) => {
    touched.current = true;
    const v = typeof next === 'function' ? next(valueRef.current) : next;
    valueRef.current = v;
    setValueState(v);
    if (store) store.prefs = { ...(store.prefs || {}), [key]: v };
    if (timer.current) clearTimeout(timer.current);
    const save = () => {
      timer.current = null;
      pending.current = null;
      // Before the migration there is nowhere to keep it.
      if (store && store.done && !store.ready) return;
      fetch(`${API_URL}/api/dashboard/preferences/screens/${encodeURIComponent(key)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        keepalive: true,
        body: JSON.stringify({ value: v === undefined ? null : v })
      }).catch(() => {});
    };
    pending.current = save;
    timer.current = setTimeout(save, SAVE_AFTER_MS);
  }, [key]);

  return [value, setValue, { loaded, ready }];
}
